import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { initSentry, addBreadcrumb as sentryBreadcrumb, captureMessage } from './lib/sentry';
import { loadFlags } from './lib/flags';
import { landingRefRedirectTarget } from './lib/referral';
import { detectLang, helpZhQueryRedirectTarget } from './lib/i18n-core';
import { canHydratePrerender } from './lib/prerender-hydration';
import App from './App';
import { LangProvider } from './lib/LangProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';

initSentry();

// One-time cleanup: the legacy referral attribution cookie (pw_ref) from the
// previous release is no longer used — invite attribution is now a visible
// register-form field. Expire any lingering cookie unconditionally so a prior
// opt-out is honored and no stale tracking persists to its 30-day TTL
// (GitHub-codex #487 P2). Host-only, Path=/ — matches how it was set.
try { document.cookie = 'pw_ref=; Max-Age=0; Path=/; SameSite=Lax'; } catch { /* no-op */ }

// #312: TanStack Query is the data layer for in-flight job tracking
// (replaces the hand-rolled sessionStorage polling in useVideo/ImagePolling).
// Module-level singleton so the query cache survives Canvas/MainPage
// remounts on project switch.
const queryClient = new QueryClient();

// Sync <html lang> with detected language. index.html ships lang="en" for the
// prerendered shell, but zh-CN users on Chrome/Edge see a "translate to Chinese"
// prompt; if they accept, the translation extension wraps text nodes and the
// next React reconcile crashes with NotFoundError on parent.insertBefore.
document.documentElement.lang = detectLang();

// Boot-await: gate React mount on /api/flags resolution. Mirrors LaunchDarkly
// asyncWithLDProvider and Statsig <StatsigProvider waitForInitialization>:
// without this, every getFlagsSync() consumer (ImageGenPanel useMemo([]),
// ModelSelector render, Canvas/MainPage useState initializers) reads
// DEFAULT_FLAGS at mount and silently downgrades flag-gated values like
// '极致' → '高级'. flags.ts catches a 5xx / network-down failure and falls
// back to DEFAULT_FLAGS so a dead /flags endpoint never blocks the SPA.
(async () => {
  // Forward a legacy/copied /?ref= invite link from the landing page straight to
  // the register form so the code reaches the field — there is no cookie bridge
  // and the landing CTAs don't carry the query string (final-codex / GitHub-codex
  // #487 P1). A full pre-mount navigation avoids a hydration mismatch against the
  // prerendered landing markup. parseRefParam validates, so a junk ?ref= is ignored.
  {
    const redirect = landingRefRedirectTarget(window.location.pathname, window.location.search);
    if (redirect) { window.location.replace(redirect); return; }
  }
  // Legacy /help/*?lang=zh links: the #467 en-mirror pin in detectLang() runs
  // before the ?lang= branch, so these would silently render English
  // (GitHub-codex #467 P2, 2026-07-18 round). Forward to the /zh mirror
  // pre-mount — same pattern as the ?ref= redirect above: keeps the zh
  // contract without breaking deterministic hydration on en-pinned pages.
  {
    const redirect = helpZhQueryRedirectTarget(window.location.pathname, window.location.search);
    if (redirect) { window.location.replace(redirect); return; }
  }
  const t0 = performance.now();
  await loadFlags();
  const ms = Math.round(performance.now() - t0);

  // Two-tier sentinel for /api/flags slowness:
  //   200ms  — breadcrumb (attaches to any subsequent error so we can
  //            correlate flag-race regressions).
  //   2000ms — captureMessage('warning') so the slowness is searchable on
  //            its own. Cold-start / weak networks can legitimately spend
  //            ~1s here; only flag truly anomalous waits.
  if (ms >= 200) sentryBreadcrumb({ category: 'boot', message: 'flags-slow', data: { ms } });
  if (ms >= 2000) captureMessage(`flags-slow ${ms}ms`, 'warning');

  const rootEl = document.getElementById('root')!;
  const app = (
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <LangProvider initialLang={detectLang()}>
            <QueryClientProvider client={queryClient}>
              <App />
            </QueryClientProvider>
          </LangProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );

  // data-ssr="1" is set by prerender.mjs for public SEO routes. Hydrate only
  // when the server markup and the client's first render share the same locale.
  const ssrLang = (rootEl.dataset.ssrLang as 'en' | 'zh' | undefined) ?? 'en';
  const currentLang = detectLang();
  const canHydrateSsr = canHydratePrerender({
    hasSsr: rootEl.dataset.ssr === '1',
    ssrLang,
    currentLang,
    path: window.location.pathname,
  });
  if (canHydrateSsr) {
    ReactDOM.hydrateRoot(rootEl, app);
  } else {
    rootEl.innerHTML = '';
    ReactDOM.createRoot(rootEl).render(app);
  }
})();
